import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SubscriptionSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [processing, setProcessing] = useState(true);
  const [success, setSuccess] = useState(false);
  const [myProfile, setMyProfile] = useState(null);

  useEffect(() => {
    if (user) {
      fetchProfile();
    }
  }, [user]);

  useEffect(() => {
    if (myProfile) {
      executeAgreement();
    }
  }, [myProfile]);

  const fetchProfile = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/researchers/user/${user.id}`);
      if (response.ok) {
        const data = await response.json();
        setMyProfile(data);
      } else {
        // No researcher profile, use user.id directly
        setMyProfile({ id: user.id });
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      // On error, use user.id as fallback
      setMyProfile({ id: user.id });
    }
  };

  const executeAgreement = async () => {
    const token = searchParams.get('token') || searchParams.get('ba_token');
    const subscriptionId = searchParams.get('subscription_id');
    
    if (!token && !subscriptionId) {
      toast({
        title: "Error",
        description: "Invalid payment token",
        variant: "destructive"
      });
      setProcessing(false);
      return;
    }

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/paypal/execute-agreement?token=${token}&researcher_id=${myProfile.id}&subscription_id=${subscriptionId}`,
        { method: 'POST' }
      );

      if (response.ok) {
        const data = await response.json();
        setSuccess(true);
        toast({
          title: "Success!",
          description: "Your subscription is now active"
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to activate subscription",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to process payment",
        variant: "destructive"
      });
    } finally {
      setProcessing(false);
    }
  };

  if (processing) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="w-96">
          <CardContent className="pt-6">
            <div className="text-center">
              <Loader2 className="h-12 w-12 animate-spin text-purple-600 mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">Processing Payment...</h2>
              <p className="text-gray-600">Please wait while we activate your subscription</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center py-12 px-4">
      <Card className="max-w-md w-full">
        <CardHeader>
          <div className="flex justify-center mb-4">
            <CheckCircle className="h-16 w-16 text-green-500" />
          </div>
          <CardTitle className="text-center text-2xl">Subscription Activated!</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-center text-gray-600">
            Thank you for subscribing! Your account has been upgraded and you now have access to all premium features.
          </p>
          
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h3 className="font-semibold text-green-900 mb-2">What's Next?</h3>
            <ul className="text-sm text-green-800 space-y-1">
              <li>✓ Your AI credits have been added</li>
              <li>✓ Virtual rooms are now available</li>
              <li>✓ All premium features unlocked</li>
            </ul>
          </div>

          <div className="flex gap-3">
            <Button 
              onClick={() => navigate('/library')} 
              className="flex-1 bg-purple-600 hover:bg-purple-700"
            >
              Go to Library
            </Button>
            <Button 
              onClick={() => navigate('/subscription')} 
              variant="outline"
              className="flex-1"
            >
              View Subscription
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionSuccess;